import { useNavigate, useLocation } from "react-router-dom";
import {
  Table,
  useBreakpoints,
  Row,
  TruncatedAccount,
  MenuButton,
} from "@narmi/design_system";
import { catcat as cc } from "cerulean";
import { useLocalization } from "@fluent/react";
import { showAmountStrikeThrough } from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import {
  showFileImportCancelOption,
  DisplayStatus,
} from "byzantine/src/dbbl/businessLogic/entities/fileImportActivity";
import type { FormattedFileImportItem } from "byzantine/src/dbbl/businessLogic/entities/fileImportActivity/types";
import { showReverseOption } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import { useUser } from "byzantine/src/dbbl/businessLogic/entities/users/hooks";
import {
  useDualApprovalPermission,
  useACHReverseCollectPermission,
  useACHReverseSendPermission,
} from "byzantine/src/dbbl/businessLogic/modules/achPayments/permissions";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import { FILTER_CONFIG, FilterChips } from "../Filters/filterConfig";
import {
  PAYMENT_ACTIVITY_ROUTE,
  getFileImportReverseRoute,
  getFileImportReverseFromDrawerRoute,
  getFileImportReversalSelectionRoute,
} from "../routes";
import { RenderDrawerProps, StatusTag, TableWithDrawer } from "../common";
import styles from "../PaymentActivity.module.scss";
import BulkPaymentDrawer from "../Bulk/BulkPaymentDrawer";
import WireImportDrawer from "./WireImportDrawer";

const FileImportTable = ({
  payments,
  handleToggleCancelDialog,
}: {
  payments: FormattedFileImportItem[];
  handleToggleCancelDialog: (
    uuid:
      | API.BulkPaymentId
      | API.WireImport.WireImportBatchId
      | API.DualApprovalId,
    type?: "ach" | "wire"
  ) => void;
}) => {
  const { m, l } = useBreakpoints();
  const location = useLocation();
  const navigate = useNavigate();
  const { l10n } = useLocalization();
  const institution = useInstitutionSettings();
  const hasDualApprovalPermission = useDualApprovalPermission();
  const hasReverseSendPermission = useACHReverseSendPermission();
  const hasReverseCollectPermission = useACHReverseCollectPermission();
  const hasReversePermission =
    hasReverseSendPermission || hasReverseCollectPermission;
  const isReversalFeatureEnabled = Boolean(
    institution.features?.user_initiated_ach_reversals
  );
  const currentUser = useUser();

  const handleOpenDrawer = (
    uuid: API.BulkPaymentId | API.WireImport.WireImportBatchId
  ) => {
    navigate(
      `/${PAYMENT_ACTIVITY_ROUTE}/file-import/${uuid}${location.search}`
    );
  };

  const renderRow = (item: FormattedFileImportItem) => {
    const {
      uuid,
      type,
      title,
      displayStatus,
      institutionAccount,
      netTotalDollarsWithSign,
      paymentCount,
      dualApprovalId,
      dateRange,
      isExpedited,
    } = item;

    const showCancel = showFileImportCancelOption(item, {
      currentUserUuid: currentUser?.id,
    });
    // only ACH file imports support reversal
    const showReverseButtons =
      type === "ach" &&
      showReverseOption(item, {
        isReversalFeatureEnabled,
        hasReversePermission,
      });
    const showStrikeThrough = showAmountStrikeThrough(displayStatus);
    const menuItems = [];

    if (type === "ach") {
      if (dualApprovalId && hasDualApprovalPermission) {
        menuItems.push(
          <MenuButton.Item
            key="view-approvals-button"
            label={l10n.getString("label-view-approvals")}
            startIcon="eye"
            onSelect={() =>
              window.location.assign(
                `/approvals/pending?dualApprovalId=${dualApprovalId}`
              )
            }
          />
        );
      } else if (paymentCount > 0) {
        menuItems.push(
          <MenuButton.Item
            key="view-payments-button"
            label={l10n.getString("view-payments-cta", {
              total: paymentCount,
            })}
            startIcon="eye"
            onSelect={() => {
              navigate({
                pathname: `/${PAYMENT_ACTIVITY_ROUTE}`,
                search: `?${
                  FILTER_CONFIG[FilterChips.BULK_PAYMENT].queryKey
                }=${uuid}`,
              });
            }}
          />
        );
      }
      if (showReverseButtons) {
        menuItems.push(
          <MenuButton.Item
            key="reverse-all-eligible-button"
            label={l10n.getString("label-reverse-all-eligible")}
            startIcon="rotate-ccw"
            onSelect={() => {
              navigate(getFileImportReverseRoute(uuid as API.BulkPaymentId));
            }}
          />,
          <MenuButton.Item
            key="select-for-reversal-button"
            label={l10n.getString("label-select-for-reversal")}
            startIcon="mouse-pointer"
            onSelect={() => {
              navigate(
                getFileImportReversalSelectionRoute(uuid as API.BulkPaymentId)
              );
            }}
          />
        );
      }
    } else if (type === "wire") {
      if (
        displayStatus === DisplayStatus.pending_approval &&
        dualApprovalId &&
        hasDualApprovalPermission
      ) {
        menuItems.push(
          <MenuButton.Item
            key="view-approvals-button"
            label={l10n.getString("label-view-approvals")}
            startIcon="eye"
            onSelect={() =>
              window.location.assign(
                `/approvals/pending?dualApprovalId=${dualApprovalId}`
              )
            }
          />
        );
      } else if (paymentCount > 0) {
        menuItems.push(
          <MenuButton.Item
            key="view-wires-button"
            label={l10n.getString("view-payments-cta", {
              total: paymentCount,
            })}
            startIcon="eye"
            onSelect={() => {
              navigate({
                pathname: `/${PAYMENT_ACTIVITY_ROUTE}`,
                search: `?${
                  FILTER_CONFIG[FilterChips.WIRE_IMPORT_BATCH].queryKey
                }=${uuid}`,
              });
            }}
          />
        );
      }
    }

    if (showCancel) {
      menuItems.push(
        <MenuButton.Item
          key="delete-button"
          label={l10n.getString("label-cancel-payment")}
          startIcon="trash-2"
          onSelect={() => {
            const cancelId =
              type === "wire" && dualApprovalId ? dualApprovalId : uuid;
            handleToggleCancelDialog(cancelId, type);
          }}
        />
      );
    }

    const typeLabel =
      type === "wire"
        ? l10n.getString("label-method-wire")
        : l10n.getString("label-method-ach");

    return (
      <Table.Row
        key={`table-row__${uuid}`}
        onRowClick={() => handleOpenDrawer(uuid)}
      >
        <Table.Cell>
          <Row>
            {!(m || l) ? (
              <Row.Item shrink>
                <StatusTag status={displayStatus} isBadge />{" "}
              </Row.Item>
            ) : null}
            <Row.Item>
              <div
                className={cc(styles.ellipsisWrapper, { "margin--left--l": m })}
              >
                <span>{title}</span>
                <div className="fontSize--s fontColor--secondary">
                  {dateRange}
                  {isExpedited && (
                    <span className="margin--left--xs narmi-icon-zap" />
                  )}
                </div>
              </div>
            </Row.Item>
          </Row>
        </Table.Cell>
        <Table.Cell>
          <StatusTag status={displayStatus} isBadge={false} />
        </Table.Cell>
        <Table.Cell>
          <TruncatedAccount
            name={institutionAccount?.name ?? ""}
            lastFour={institutionAccount?.getMaskedNumber() ?? ""}
          />
        </Table.Cell>
        <Table.Cell>{typeLabel}</Table.Cell>
        <Table.Cell textAlign={"right" as any}>
          <span
            className={cc({
              [styles.strikeThrough]: showStrikeThrough,
            })}
          >
            {netTotalDollarsWithSign}
          </span>
        </Table.Cell>
        <Table.Cell>
          <div
            style={{ alignSelf: "end" }}
            onClick={(e) => e.stopPropagation()}
          >
            {menuItems.length > 0 && (
              <MenuButton triggerIcon="more-vertical">{menuItems}</MenuButton>
            )}
          </div>
        </Table.Cell>
      </Table.Row>
    );
  };

  const renderDrawer: RenderDrawerProps<FormattedFileImportItem> = (
    payment,
    paymentId,
    { onClose, onNext, onPrev, isOpen }
  ) => {
    if (payment?.type === "wire") {
      return (
        <WireImportDrawer
          isOpen={isOpen}
          item={payment}
          itemUuid={paymentId as string}
          handleClose={onClose}
          handleOnNext={onNext}
          handleOnPrev={onPrev}
          handleToggleCancelDialog={handleToggleCancelDialog}
        />
      );
    }
    if (!payment || payment.type !== "ach") {
      return null;
    }
    return (
      <BulkPaymentDrawer
        isOpen={isOpen}
        handleClose={onClose}
        handleOnNext={onNext}
        handleOnPrev={onPrev}
        handleToggleCancelDialog={(uuid) =>
          handleToggleCancelDialog(uuid, "ach")
        }
        payment={null}
        paymentUuid={paymentId as API.BulkPaymentId}
        handleReverseAllEligible={(uuid) =>
          navigate(getFileImportReverseFromDrawerRoute(uuid))
        }
        handleSelectForReversal={(uuid) =>
          navigate(getFileImportReversalSelectionRoute(uuid))
        }
      />
    );
  };

  return (
    <TableWithDrawer
      items={payments}
      renderRow={renderRow}
      renderDrawer={renderDrawer}
      basePath={`${PAYMENT_ACTIVITY_ROUTE}/file-import`}
      paramKey="fileImportId"
    />
  );
};

export default FileImportTable;
